import React, { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URLS } from "../constants";
import { initializeAuth } from "../utils/auth";
import { authenticatedFetch } from "../utils/api";
import { sessionManager } from "../auth/sessionManager";
import {
  markAuthReady,
  isAuthReady as readinessFlag,
} from "../auth/authReadiness";
import { User } from "../types/auth";
import { initializeCsrfToken } from "../utils/csrf";

interface AuthContextType {
  isLoggedIn: boolean;
  authState: "loading" | "authenticated" | "unauthenticated";
  user: User | null;
  setUser: (user: User | null) => void;
  logout: () => void;
  loginWithCredentials: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; error?: string }>;
  updateLanguage: (language: "en" | "es") => Promise<boolean>;
  checkSession: (force?: boolean) => Promise<boolean>;
  isAuthReady: () => boolean; // NEW readiness accessor
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [authState, setAuthState] = useState<
    "loading" | "authenticated" | "unauthenticated"
  >("loading");
  const navigate = useNavigate();

  // Wrapper around sessionManager
  const checkSession = async (force = false): Promise<boolean> => {
    const result = await sessionManager.refresh(force);
    const userObj = sessionManager.getUser();
    setUser(userObj);
    setIsLoggedIn(result);
    setAuthState((prev) =>
      result ? "authenticated" : prev === "loading" ? "unauthenticated" : prev
    );
    // Mark readiness after first resolution
    markAuthReady();
    return result;
  };

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const isAuthenticated = await checkSession();
        // If user is authenticated and we're on auth pages (except verification pages), redirect to dashboard
        if (
          isAuthenticated &&
          window.location.pathname.startsWith("/auth") &&
          !window.location.pathname.includes("/verify") &&
          !window.location.pathname.includes("/callback")
        ) {
          navigate("/");
        }
      } catch (error) {
        // Only log initialization errors in development
        if (process.env.NODE_ENV === "development") {
          console.error("[Auth] Initialization failed:", error);
        }
        setAuthState("unauthenticated");
        markAuthReady();
      }
    };

    // Add timeout to prevent infinite loading state
    const authTimeout = setTimeout(() => {
      if (authState === "loading") {
        // Only log timeout warnings in development
        if (process.env.NODE_ENV === "development") {
          console.warn(
            "[Auth] Initialization timed out, assuming unauthenticated"
          );
        }
        setAuthState("unauthenticated");
        markAuthReady();
      }
    }, 10000); // 10 second timeout

    initAuth();
    initializeAuth(navigate);
    
    // Initialize CSRF token for authenticated requests
    initializeCsrfToken().catch(error => {
      console.error('Failed to initialize CSRF token:', error);
    });

    // Cleanup timeout when component unmounts or auth completes
    return () => {
      clearTimeout(authTimeout);
    };
  }, [authState, navigate]);

  // Separate effect for periodic session checks - only when logged in
  // Subscribe to sessionManager updates (in case external triggers modify it)
  useEffect(() => {
    const unsubscribe = sessionManager.subscribe((result) => {
      setIsLoggedIn(result.authenticated);
      if (result.authenticated) {
        setAuthState("authenticated");
        setUser(result.user);
      } else {
        setAuthState((prev) => (prev === "loading" ? "unauthenticated" : prev));
        setUser(null);
      }
    });
    return () => {
      unsubscribe();
    };
  }, []);

  // No internal periodic timer; sessionManager TTL + caller-driven refreshes suffice.

  const handlePostLoginRedirection = () => {
    const redirectPath = localStorage.getItem("redirectAfterLogin");
    if (redirectPath) {
      localStorage.removeItem("redirectAfterLogin");
      navigate(redirectPath);
    } else {
      navigate("/");
    }
  };

  // Cookie-based login method with route persistence
  const loginWithCredentials = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await fetch(API_URLS.AUTH.LOGIN, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsLoggedIn(true);
        setAuthState("authenticated");

        setTimeout(() => {
          handlePostLoginRedirection();
        }, 800);
        return { success: true };
      } else {
        const errorData = await response.json();
        return { success: false, error: errorData.message || "Login failed" };
      }
    } catch {
      return { success: false, error: "Network error occurred" };
    }
  };

  // Clear all authentication data
  const clearAllAuthData = () => {
    // Clear localStorage
    localStorage.removeItem("jwt_token");
    localStorage.removeItem("redirectAfterLogin");
    localStorage.removeItem("sso_return_url");

    // Clear all MSAL tokens
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("msal")) {
        localStorage.removeItem(key);
      }
    });

    // Clear sessionStorage
    sessionStorage.clear();

    // Note: auth_token cookie is httpOnly and cannot be cleared by frontend
    // It will be cleared by the backend logout endpoint when needed
    // This is the correct behavior for security
  };

  const updateLanguage = async (language: "en" | "es") => {
    try {
      const response = await authenticatedFetch("/api/users/me/language", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language }),
      });

      if (response.ok) {
        const data = await response.json();
        setUser((prev) =>
          prev ? { ...prev, preferred_language: data.language } : prev
        );
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  // Logout only by explicit user action
  const logout = async () => {
    try {
      await fetch(API_URLS.AUTH.LOGOUT, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });
    } catch {
      // Ignore logout errors - user will be logged out locally regardless
    } finally {
      clearAllAuthData();
      setIsLoggedIn(false);
      setUser(null);
      setAuthState("unauthenticated");
      sessionManager.invalidate();
      // Redirect to unified auth page (login/signup)
      navigate("/auth/login");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        authState,
        user,
        setUser,
        logout,
        loginWithCredentials,
        updateLanguage,
        checkSession,
        isAuthReady: readinessFlag,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
